import { useDocumentGroupContext } from '@/finsight/application/Research/DocumentGroupContext';
import { replace } from 'connected-next-router';
import { useDispatch, useSelector } from 'react-redux';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import merge from 'lodash/merge';
import createAsyncRequest from '@/Framework/State/createAsyncRequest';
import { makeEmptyGroupIds } from '@/finsight/application/actions/research/helpers';
import { useDIContext } from '@/Framework/DI/DIContext';
import { filterResearchResponse } from '@/finsight/application/Research/serverSide/helpers';
import ResearchRepository from '@/finsight/infrastructure/repository/ResearchRepository';
import DocumentGroup, { DOCUMENT_GROUP_NAMES } from '@/finsight/domain/DocumentGroup';
import {
  getFiltersPayload,
  getResearchFiltersInitialState,
  payloadChange,
  resetResearchFilter,
} from '@/finsight/application/actions/research/filters/actions';
import researchFiltersSelectors from '@/finsight/application/actions/research/filters/selectors';
import URL from '@/finsight/Router/urlConstants';
import { RESEARCH_HEADER_MAPPING_NAME } from '@/finsight/application/actions/research/constants';
import { isModuleEnabled } from '@/finsight/application/actions/header/helpers';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';

const stateSelector = (state) => ({
  filtersPayload: researchFiltersSelectors.getFiltersPayload(state),
});

export default function useResearchTableActions(state, setResearchData, requestPayload, searchFieldValue) {
  const { container } = useDIContext();
  const dispatch = useDispatch();
  const researchRepository = container.get(ResearchRepository);
  const documentGroupService = container.get(DocumentGroup);
  const { search, pathname } = useSelector((store: any) => store.router.location);
  const { mapping } = useDictionariesContext();

  const { filtersPayload } = useSelector(stateSelector);
  const {
    collection: documentGroupsCollection,
    documentGroup: documentGroupsItem,
    setDocumentGroup,
    setEmptyDocumentGroupIds,
  } = useDocumentGroupContext();

  const getDocumentById = async (documentId) => {
    let response;
    try {
      let documentWindow = window.open('', '_blank');
      response = await researchRepository.getResearchDocument(documentId);
      documentWindow.focus();

      if (response.documentUrl) {
        documentWindow.location = response.documentUrl;
      } else {
        documentWindow.close();
      }
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    }
    return response;
  };

  const getResearchList = (payloadData) => {
    const payload = prepareResearchPayload(payloadData, requestPayload);

    const request = createAsyncRequest({
      method: researchRepository.getResearchList,
      callbacks: {
        onSuccess: ({ response }) => {
          setResearchData((prev) => ({
            ...prev,
            ...response,
          }));
        },
        onError: (error) => AlertManager.error(getErrorMessage(error)),
      },
    });

    request(payload);
  };

  const getResearchForAllGroups = (payloadData) => {
    const payload = prepareResearchPayload(payloadData, requestPayload);
    const request = createAsyncRequest({
      method: researchRepository.getResearchTileData,
      callbacks: {
        onSuccess: ({ response }) => {
          const emptyGroupIds = makeEmptyGroupIds(response);
          setEmptyDocumentGroupIds(emptyGroupIds);
          setResearchData((prev) => ({
            ...prev,
            ...filterResearchResponse(response, payload.filter.includedDocumentGroupId),
          }));
        },
        onError: ({ error }) => {
          !Dispatcher.isAbortError(error) && AlertManager.error(getErrorMessage(error));
        },
      },
    });

    request(payload);
  };

  const sort = (sortBy, sortOrder) => setResearchData((prev) => ({ ...prev, sortBy, sortOrder }));

  const paginate = (page) => setResearchData((prev) => ({ ...prev, page }));

  const itemsPerPage = (perPage) => setResearchData((prev) => ({ ...prev, perPage }));

  const documentGroupChanged = (documentGroup, isTile) => {
    const document = documentGroupService.getByGroupName(documentGroupsCollection, documentGroup);
    if (documentGroupsItem?.groupName !== documentGroup) {
      setDocumentGroup(document);
      if (isTile) {
        const payload = {
          ...requestPayload,
          page: 1,
          search: searchFieldValue,
          filter: {
            ...getResearchFiltersInitialState(),
            ...requestPayload.filter,
            includedDocumentGroupId: document.groupId,
          },
        };

        getResearchList(payload);
      }
      paginate(1);
      if (pathname.includes(URL.NRSRO_RESEARCH)) {
        dispatch(replace(`${ URL.NRSRO_RESEARCH }/${ documentGroup.toLocaleLowerCase() }${ search }`));
      }
    }
  };

  const resetResearch = () => (dispatch) => {
    dispatch(resetResearchFilter());
  };

  const initResearchModule = async (
    store,
    groupName = DOCUMENT_GROUP_NAMES.ALL,
  ) => {
    const {
      productIds,
      regionIds,
    } = store.pageFilters;

    if (!isModuleEnabled(mapping, productIds, regionIds, RESEARCH_HEADER_MAPPING_NAME)) {
      return;
    }

    let documentGroup = documentGroupService.getDefault();
    if (documentGroupService.isValid(documentGroupsCollection, groupName)) {
      documentGroup = documentGroupService.getByGroupName(documentGroupsCollection, groupName);
    }

    let filtersState = getResearchFiltersInitialState();
    let initialFiltersPayload = dispatch(getFiltersPayload(filtersState));

    let filtersPayload = merge(initialFiltersPayload, requestPayload);

    if (Object.keys(filtersPayload).length) {
      dispatch(payloadChange(filtersPayload));
    }

    dispatch(setDocumentGroup(documentGroup));
    itemsPerPage(10);
    getResearchForAllGroups({});
  };

  const prepareResearchPayload = (payloadData, requestPayload) => {
    const { sortBy, sortOrder, page, perPage } = state;

    return ({
      ...requestPayload,
      filter: {
        ...filtersPayload || getResearchFiltersInitialState(),
        ...requestPayload.filter,
        includedDocumentGroupId: documentGroupsItem ? documentGroupsItem.groupId : documentGroupsItem,
      },
      sortBy,
      sortOrder,
      page,
      perPage,
      search: searchFieldValue,
      ...payloadData,
    });
  };

  return {
    getDocumentById,
    getResearchList,
    getResearchForAllGroups,
    sort,
    paginate,
    itemsPerPage,
    documentGroupChanged,
    resetResearch,
    initResearchModule,
  };
}
